import { cn } from '@neo/lib/utils'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Typography } from '@neo/primitives/typography'
import { ExternalLink } from 'lucide-react'
import { type FC, useState } from 'react'
import { useConfigureNumberMutation } from 'v2/pages/settings/phone-numbers/queries'
import { errorToast, successToast } from 'v2/utils/toasts'
import { buildSandboxConnectPayload, type SandboxConnectNumber } from './sandbox-connect-payload'
import { getConnectCopy, type SandboxConnectKind } from './sandbox-setup-copy'
import { useSandboxConnectOptions } from './use-sandbox-connect-options'

/**
 * The selection dot, drawn rather than a styled native radio - the real input is
 * `sr-only` beside it, so behaviour is native while the visual stays on the
 * token scale. Same component the KYC application picker uses.
 */
interface SelectionRadioProps {
  isSelected: boolean
}

const SelectionRadio: FC<SelectionRadioProps> = ({ isSelected }) => (
  <span
    aria-hidden="true"
    className={cn(
      'flex size-4 shrink-0 items-center justify-center rounded-full border',
      isSelected ? 'border-primary' : 'border-hv3',
    )}
  >
    {isSelected && <span className="size-2 rounded-full bg-primary" />}
  </span>
)

interface SandboxConnectDialogProps {
  /** The open dialog's kind. `undefined` means closed - there is one dialog, not three. */
  kind?: SandboxConnectKind
  onClose: () => void
  /** E.164 sandbox number; the PATCH path segment. */
  sandboxNumber?: string
  /** Fields the PATCH must carry through unchanged. */
  numberFields?: SandboxConnectNumber
  /**
   * Currently attached id, so reopening preselects what is already wired. Read
   * at mount only; the caller keys this component on `kind` so each open is a
   * fresh mount.
   */
  currentId?: string
  /** Opens the create flow for this kind (side sheet, or a new tab for agents). */
  onCreate: (kind: SandboxConnectKind) => void
}

/**
 * "Connect an AI Agent / application / SIP trunk" - step 2 of the sandbox setup.
 *
 * One component for all three kinds: the shape is identical and only the copy,
 * the list source and the id field differ, all of which are already tabulated.
 *
 * The list follows the pick-one-of-your-saved-X row pattern rather than a table
 * (see `pages/CLAUDE.md`): one primary value per row, a real radio group under a
 * drawn dot, and the create action in the footer opposite the primary button
 * because creating is the alternative to picking.
 */
export const SandboxConnectDialog = ({
  kind,
  onClose,
  sandboxNumber,
  numberFields,
  currentId,
  onCreate,
}: SandboxConnectDialogProps) => {
  const { options, truncated, isPending, isError } = useSandboxConnectOptions(kind)
  const { mutate, isPending: isSaving } = useConfigureNumberMutation(sandboxNumber ?? '')

  /**
   * Seeded once per mount. Reopening for a different kind must not keep the
   * previous kind's selection - that would post an application id as a trunk id
   * - and the caller guarantees a fresh mount by keying this on the open kind.
   * A key beats both an effect (which renders one frame holding the stale id)
   * and a render-phase reset (which needs a second state just to remember what
   * it last saw).
   */
  const [selected, setSelected] = useState(currentId ?? '')

  if (!kind) return null

  const copy = getConnectCopy(kind)
  const isEmpty = !isPending && !isError && options.length === 0

  const connectDisabledReason = (): string | undefined => {
    if (isSaving) return undefined
    if (!sandboxNumber) return 'Your sandbox number has not been assigned yet'
    if (!selected) return 'Pick one to continue'
    return undefined
  }

  const handleConnect = () => {
    if (!sandboxNumber || !selected) return
    mutate(buildSandboxConnectPayload(kind, selected, numberFields), {
      onSuccess: () => {
        successToast({
          title: 'Connected',
          description: `${copy.connected} now handles calls on your sandbox number.`,
        })
        onClose()
      },
      onError: (error) =>
        errorToast({
          title: 'Could not connect',
          description: error instanceof Error ? error.message : 'Please try again.',
        }),
    })
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <Typography variant="S/400" className="text-secondary">
            {copy.subtitle}
          </Typography>
        </DialogHeader>

        <div className="border-t border-b py-2">
          {isPending && (
            <Typography variant="S/400" className="px-3 py-3 text-muted-foreground">
              Loading…
            </Typography>
          )}
          {isError && (
            <Typography
              variant="S/400"
              className="px-3 py-3 text-muted-foreground"
              data-testid="text-connect-error"
            >
              Could not load the list. Close this and try again.
            </Typography>
          )}
          {isEmpty && (
            <Typography
              variant="S/400"
              className="px-3 py-3 text-muted-foreground"
              data-testid="text-connect-empty"
            >
              {copy.empty}
            </Typography>
          )}
          {!isPending && !isError && options.length > 0 && (
            <fieldset className="min-w-0">
              <legend className="sr-only">{copy.title}</legend>
              {options.map((option) => {
                const isSelected = option.id === selected
                return (
                  <label
                    key={option.id}
                    className={cn(
                      'group flex cursor-pointer items-center gap-3 px-3 py-3',
                      // The input is visually hidden, so keyboard focus would
                      // otherwise be invisible.
                      'has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-primary',
                    )}
                  >
                    <input
                      type="radio"
                      name={`sandbox-connect-${kind}`}
                      className="sr-only"
                      checked={isSelected}
                      onChange={() => setSelected(option.id)}
                      data-testid={`radio-connect-${option.id}`}
                    />
                    <SelectionRadio isSelected={isSelected} />
                    <Typography
                      variant={isSelected ? 'P/500' : 'P/400'}
                      className={cn(
                        'min-w-0 flex-1 truncate transition-colors',
                        isSelected
                          ? 'text-foreground'
                          : 'text-muted-foreground group-hover:text-secondary',
                      )}
                      title={option.label || undefined}
                    >
                      {option.label || '-'}
                    </Typography>
                  </label>
                )
              })}
            </fieldset>
          )}
          {truncated && (
            <Typography
              variant="S/400"
              className="px-3 pt-2 text-muted-foreground"
              data-testid="text-connect-truncated"
            >
              Showing the first {options.length}. Use the full list if the one you want is missing.
            </Typography>
          )}
        </div>

        <DialogFooter className="items-center justify-between sm:justify-between">
          <Button
            variant="ghostLink"
            className="px-0"
            onClick={() => onCreate(kind)}
            data-testid={`button-connect-create-${kind}`}
          >
            {copy.createLabel}
            <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </Button>
          <ButtonWithLoader
            onClick={handleConnect}
            loading={isSaving}
            disabled={!selected || !sandboxNumber}
            disabledReason={connectDisabledReason()}
            data-testid="button-connect-confirm"
          >
            Connect
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
